import { ApplicationMenu } from '../types';
import type { ApplicationMenuType } from '../types';
import { Instant } from '@js-joda/core';
import { DEFAULT_GROUP_CONFIG, initialToastState } from '@thinkalpha/ui-types/types/toast';
import type { ToastState } from '@thinkalpha/ui-types/types/toast';
import type { AppAction } from 'src/store/actions';
import type { SnackbarMessage, SnackbarOptions } from 'src/store/actions/ui';

export interface SymbolCacheEntry {
    symbol: string;
    lastUse: Instant;
}

export interface UIState {
    superuserAccessEnabledUntil?: Instant;
    /** @deprecated Use toasts instead - kept for migration period */
    snackbarMessage?: {
        message: SnackbarMessage;
        options?: SnackbarOptions;
    };
    menus: {
        application: ApplicationMenuType;
    };
    symbolCache: SymbolCacheEntry[];
    alwaysOnTop: boolean;
    blockedWidgets: boolean;
    toasts: ToastState;
}

export const initialState: UIState = {
    superuserAccessEnabledUntil: undefined,
    symbolCache: [],
    snackbarMessage: undefined,
    menus: {
        application: ApplicationMenu.loggedOutClosed,
    },
    alwaysOnTop: false,
    blockedWidgets: false,
    toasts: initialToastState,
};

export const ui = (state = initialState, action: AppAction): UIState => {
    switch (action.type) {
        case 'masq::access-token::set': {
            return { ...state, superuserAccessEnabledUntil: undefined };
        }
        case 'superuser::set-expiration': {
            return { ...state, superuserAccessEnabledUntil: action.superuserAccessEnabledUntil };
        }

        case 'symbol-cache::set': {
            return { ...state, symbolCache: action.symbolCache };
        }

        case 'menu::set-application-menu':
            return {
                ...state,
                menus: {
                    ...state.menus,
                    application: action.menu,
                },
            };

        case 'symbol-cache::add': {
            const newCache = [...state.symbolCache];
            const existingIdx = newCache.findIndex((x) => x.symbol === action.symbol);
            if (existingIdx !== -1) {
                newCache[existingIdx] = { ...newCache[existingIdx], lastUse: Instant.now() };
            } else {
                newCache.push({ symbol: action.symbol, lastUse: Instant.now() });
            }
            return { ...state, symbolCache: newCache };
        }

        case 'symbol-cache::remove': {
            return {
                ...state,
                symbolCache: state.symbolCache.filter((x) => x.symbol !== action.symbol),
            };
        }

        case 'app-always-on-top': {
            return { ...state, alwaysOnTop: action.alwaysOnTop };
        }

        case 'widgets::blocked': {
            return { ...state, blockedWidgets: action.blocked };
        }

        // Toast actions
        case 'toast::enqueue': {
            const { toast: toastInput } = action;
            const { group } = toastInput;
            const groupConfig = state.toasts.config.groups[group] ?? DEFAULT_GROUP_CONFIG;

            // Determine autoHideDuration default from group config
            // If group persists, default to null (no auto-hide), otherwise use group/global default
            const defaultAutoHideDuration = groupConfig.persist
                ? null
                : (groupConfig.defaultDuration ?? state.toasts.config.defaultAutoHideDuration);

            // Apply defaults for optional fields
            const toast = {
                ...toastInput,
                id: toastInput.id ?? crypto.randomUUID(),
                variant: toastInput.variant ?? 'info',
                createdAt: toastInput.createdAt ?? Date.now(),
                open: toastInput.open ?? true,
                template: toastInput.template ?? groupConfig.template ?? 'default',
                autoHideDuration: toastInput.autoHideDuration ?? defaultAutoHideDuration,
            };

            // Get current queue for this group
            const currentQueue = state.toasts.groupQueues[group] ?? [];

            // Add new toast to front of queue (newest first)
            let newQueue = [toast.id, ...currentQueue];

            // Remove oldest messages if exceeding max stack
            const toRemove: string[] = [];
            if (newQueue.length > groupConfig.maxStack) {
                toRemove.push(...newQueue.slice(groupConfig.maxStack));
                newQueue = newQueue.slice(0, groupConfig.maxStack);
            }

            // Build new messages map
            const newMessages = { ...state.toasts.messages, [toast.id]: toast };
            for (const id of toRemove) {
                delete newMessages[id];
            }

            return {
                ...state,
                toasts: {
                    ...state.toasts,
                    messages: newMessages,
                    groupQueues: {
                        ...state.toasts.groupQueues,
                        [group]: newQueue,
                    },
                },
            };
        }

        case 'toast::dismiss': {
            const message = state.toasts.messages[action.id];
            if (!message) return state;

            return {
                ...state,
                toasts: {
                    ...state.toasts,
                    messages: {
                        ...state.toasts.messages,
                        [action.id]: { ...message, open: false },
                    },
                },
            };
        }

        case 'toast::dismiss-group': {
            const queue = state.toasts.groupQueues[action.group] ?? [];
            if (queue.length === 0) return state;

            const newMessages = { ...state.toasts.messages };
            for (const id of queue) {
                if (newMessages[id]) {
                    newMessages[id] = { ...newMessages[id], open: false };
                }
            }

            return {
                ...state,
                toasts: {
                    ...state.toasts,
                    messages: newMessages,
                },
            };
        }

        case 'toast::dismiss-all': {
            const newMessages: typeof state.toasts.messages = {};
            for (const [id, msg] of Object.entries(state.toasts.messages)) {
                newMessages[id] = { ...msg, open: false };
            }

            return {
                ...state,
                toasts: {
                    ...state.toasts,
                    messages: newMessages,
                },
            };
        }

        case 'toast::dismiss-hidden':
        case 'toast::dismiss-visible': {
            // Get sorted toasts to determine which are visible vs hidden
            const allToasts = Object.values(state.toasts.messages).filter((t) => t.open);
            const sortedToasts = [...allToasts].sort((a, b) => {
                const priorityA = state.toasts.config.groups[a.group]?.priority ?? 99;
                const priorityB = state.toasts.config.groups[b.group]?.priority ?? 99;
                if (priorityA !== priorityB) return priorityA - priorityB;
                return b.createdAt - a.createdAt;
            });

            const targetToasts =
                action.type === 'toast::dismiss-hidden'
                    ? sortedToasts.slice(state.toasts.config.maxVisible)
                    : sortedToasts.slice(0, state.toasts.config.maxVisible);

            if (targetToasts.length === 0) return state;

            const newMessages = { ...state.toasts.messages };
            for (const toast of targetToasts) {
                newMessages[toast.id] = { ...newMessages[toast.id], open: false };
            }

            return {
                ...state,
                toasts: {
                    ...state.toasts,
                    messages: newMessages,
                },
            };
        }

        case 'toast::remove': {
            const message = state.toasts.messages[action.id];
            if (!message) return state;

            const { [action.id]: _removed, ...remainingMessages } = state.toasts.messages;
            const newQueues = { ...state.toasts.groupQueues };

            // Remove from group queue
            if (newQueues[message.group]) {
                newQueues[message.group] = newQueues[message.group].filter((id) => id !== action.id);
                // Clean up empty queues
                if (newQueues[message.group].length === 0) {
                    delete newQueues[message.group];
                }
            }

            return {
                ...state,
                toasts: {
                    ...state.toasts,
                    messages: remainingMessages,
                    groupQueues: newQueues,
                },
            };
        }

        case 'toast::configure': {
            const mergedGroups = { ...state.toasts.config.groups };
            if (action.config.groups) {
                for (const [key, value] of Object.entries(action.config.groups)) {
                    mergedGroups[key] = { ...mergedGroups[key], ...value };
                }
            }
            return {
                ...state,
                toasts: {
                    ...state.toasts,
                    config: {
                        ...state.toasts.config,
                        ...action.config,
                        groups: mergedGroups,
                    },
                },
            };
        }

        default:
            return state;
    }
};
